import React, { useContext, useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
	Button,
	ButtonGroup,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
} from "@material-ui/core";
import { Replay, EventNote, Delete, LocalOffer, OpenInNew, Storefront } from "@material-ui/icons";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import UndoRoundedIcon from "@material-ui/icons/UndoRounded";
import PeopleIcon from "@material-ui/icons/People";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckIcon from "@material-ui/icons/Check";
import MarkunreadIcon from "@material-ui/icons/Markunread";
import PersonIcon from "@material-ui/icons/Person";
import LockIcon from "@material-ui/icons/Lock";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import AppointmentModal from "../AppointmentModal";
import TagsModal from "../TagsModal";
import ButtonWithSpinner from "../ButtonWithSpinner";
import NegocioCustomerMenu from "./NegocioCustomerMenu";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { useTicketPopup } from "../../context/TicketPopup/TicketPopupContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import useSettings, { isSettingTruthy } from "../../hooks/useSettings";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "0 0 auto",
		alignSelf: "center",
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		gap: 6,
		flexWrap: "nowrap",
		maxWidth: "52%",
		overflow: "visible",
		[theme.breakpoints.down("sm")]: {
			maxWidth: "none",
			width: "auto",
			justifyContent: "flex-end",
			padding: 0,
			gap: 4,
			flexWrap: "nowrap",
			overflow: "visible",
		},
		"& > *": {
			margin: 0,
			flexShrink: 0,
		},
	},
	resolveGroup: {
		borderRadius: 999,
		overflow: "hidden",
		boxShadow: "none",
		"& .MuiButton-root": {
			boxShadow: "none",
		},
	},
	resolveButton: {
		minHeight: 32,
		padding: "4px 14px",
		borderRadius: "999px 0 0 999px",
		textTransform: "none",
		fontWeight: 700,
		fontSize: 13,
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
		[theme.breakpoints.down("sm")]: {
			minHeight: 30,
			padding: "3px 10px",
			fontSize: 12,
		},
	},
	resolveMenuButton: {
		minWidth: 32,
		width: 32,
		padding: 0,
		borderRadius: "0 999px 999px 0",
		borderLeft: "1px solid rgba(255,255,255,0.28)",
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
		"& .MuiButton-label": {
			minWidth: 0,
		},
		[theme.breakpoints.down("sm")]: {
			minWidth: 28,
			width: 28,
		},
	},
	menuPaper: {
		marginTop: 6,
		borderRadius: 12,
		minWidth: 240,
		boxShadow:
			theme.mode === "dark"
				? "0 12px 32px rgba(0,0,0,0.45)"
				: "0 12px 32px rgba(15,23,42,0.16)",
	},
	menuItem: {
		fontSize: 14,
		minHeight: 40,
		paddingTop: 6,
		paddingBottom: 6,
		"& .MuiListItemIcon-root": {
			minWidth: 34,
			color: theme.palette.text.secondary,
		},
		"& .MuiListItemText-primary": {
			fontSize: 14,
			fontWeight: 500,
		},
	},
	dangerItem: {
		color: theme.palette.error.main,
		"& .MuiListItemIcon-root": {
			color: theme.palette.error.main,
		},
	},
	modernIconButton: {
		padding: 6,
		margin: 0,
		minWidth: 32,
		width: 32,
		height: 32,
		borderRadius: 8,
		backgroundColor: "transparent",
		border: "none",
		color: theme.palette.text.secondary,
		"&:hover": {
			backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f1f3f9",
			color: theme.palette.primary.main,
		},
		"& svg": {
			fontSize: 20,
		},
		[theme.breakpoints.down("sm")]: {
			display: "none",
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket, embedded = false, onOpenContactDetails }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	const { openPopup } = useTicketPopup() || {};
	const { setShowSelectMessageCheckbox } = useContext(ForwardMessageContext) || {};
	const isMounted = useRef(true);
	const pendingCloseExtraRef = useRef({});
	const { getAll } = useSettings();

	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [closeTicketConfirmOpen, setCloseTicketConfirmOpen] = useState(false);
	const [removeTagsOnCloseEnabled, setRemoveTagsOnCloseEnabled] = useState(false);
	const [sendTicketCloseMessageEnabled, setSendTicketCloseMessageEnabled] =
		useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
	const [tagsModalOpen, setTagsModalOpen] = useState(false);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [negocioCustomer, setNegocioCustomer] = useState(null);
	const [negocioOpen, setNegocioOpen] = useState(false);

	const closeActionMenu = () => setMenuAnchorEl(null);

	const handleOpenAppointmentModal = () => {
		closeActionMenu();
		setAppointmentModalOpen(true);
	};

	const openTicketInPopup = (e) => {
		if (e) e.stopPropagation();
		closeActionMenu();
		const uuid = ticket?.uuid;
		if (!uuid || String(uuid).trim() === "") {
			toast.info(i18n.t("ticketOptionsMenu.openInNewUnavailable"));
			return;
		}
		openPopup(ticket);
	};

	const handleCloseAppointmentModal = () => {
		setAppointmentModalOpen(false);
	};

	const handleOpenTransferModal = () => {
		closeActionMenu();
		setTransferTicketModalOpen(true);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenTagsModal = () => {
		closeActionMenu();
		setTagsModalOpen(true);
	};

	const handleCloseTagsModal = () => {
		if (isMounted.current) {
			setTagsModalOpen(false);
		}
	};

	const handleOpenConfirmationModal = () => {
		closeActionMenu();
		setConfirmationOpen(true);
	};

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await getAll();
				const rowTags = list.find((s) => s.key === "removeTagsOnCloseTicket");
				const rowMsg = list.find((s) => s.key === "sendTicketCloseMessage");
				if (!cancelled) {
					setRemoveTagsOnCloseEnabled(
						rowTags ? isSettingTruthy(rowTags.value) : false
					);
					setSendTicketCloseMessageEnabled(
						rowMsg ? isSettingTruthy(rowMsg.value) : false
					);
				}
			} catch (_e) {
				/* noop */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [getAll]);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			setConfirmationOpen(false);
			const path = window.location.pathname || "";
			const openHere =
				(ticket.uuid && path.includes(`/tickets/${ticket.uuid}`)) ||
				(ticket.id && path.includes(`/tickets/${ticket.id}`));
			if (openHere) {
				setCurrentTicket({ id: null, code: null });
				history.push("/tickets");
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateTicketStatus = async (e, status, userId, extra = {}) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				useIntegration: status === "closed" ? false : ticket.useIntegration,
				promptId: status === "closed" ? null : ticket.promptId,
				integrationId: status === "closed" ? null : ticket.integrationId,
				...extra,
			});

			if (!isMounted.current) return;

			setLoading(false);
			if (status === "open") {
				setCurrentTicket({ ...ticket, code: "#open" });
			} else if (status === "closed") {
				setCurrentTicket({ id: null, code: null });
			} else {
				setCurrentTicket({ id: null, code: null });
			}
		} catch (err) {
			if (!isMounted.current) return;
			setLoading(false);
			toastError(err);
		}
	};

	const needsCloseConfirmModal =
		removeTagsOnCloseEnabled || sendTicketCloseMessageEnabled;

	const getCloseConfirmModalBody = () => {
		const tags = removeTagsOnCloseEnabled;
		const snd = sendTicketCloseMessageEnabled;
		if (tags && snd) {
			return i18n.t("ticketsList.buttons.confirmCloseBodyTagsAndMessage");
		}
		if (tags) {
			return i18n.t("ticketsList.buttons.confirmCloseWithTagsMessage");
		}
		if (snd) {
			return i18n.t("ticketsList.buttons.confirmCloseWithSendMessage");
		}
		return "";
	};

	const handleResolveClick = (e, extra = {}) => {
		closeActionMenu();
		if (needsCloseConfirmModal) {
			e?.stopPropagation?.();
			pendingCloseExtraRef.current = extra;
			setCloseTicketConfirmOpen(true);
		} else {
			handleUpdateTicketStatus(e, "closed", user?.id, extra);
		}
	};

	const handleConfirmCloseTicketModal = () => {
		setCloseTicketConfirmOpen(false);
		const extra = pendingCloseExtraRef.current || {};
		pendingCloseExtraRef.current = {};
		handleUpdateTicketStatus({ stopPropagation: () => {} }, "closed", user?.id, extra);
	};

	const handleMarkAsUnread = async () => {
		closeActionMenu();
		try {
			await api.put(`/tickets/${ticket.id}`, { unreadMessages: 1 });
			toast.success("Ticket marcado como no leído");
		} catch (err) {
			toastError(err);
		}
	};

	const handleKeepWithMe = async () => {
		closeActionMenu();
		try {
			await api.put(`/tickets/${ticket.id}`, { userId: user?.id });
			toast.success("Ticket asignado a ti");
		} catch (err) {
			toastError(err);
		}
	};

	const handleSelectMessages = () => {
		closeActionMenu();
		if (typeof setShowSelectMessageCheckbox === "function") {
			setShowSelectMessageCheckbox(true);
		}
	};

	const handleOpenContactDetails = () => {
		closeActionMenu();
		if (typeof onOpenContactDetails === "function") {
			onOpenContactDetails();
		}
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const renderResolveMenu = () => (
		<Menu
			anchorEl={menuAnchorEl}
			open={Boolean(menuAnchorEl)}
			onClose={closeActionMenu}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			getContentAnchorEl={null}
			PaperProps={{ className: classes.menuPaper }}
		>
			{negocioCustomer && (
				<MenuItem className={classes.menuItem} onClick={() => { closeActionMenu(); setNegocioOpen(true); }}>
					<ListItemIcon><Storefront fontSize="small" /></ListItemIcon>
					<ListItemText primary="Cliente de Negocio" secondary={`${(negocioCustomer.subscriptions || []).length} servicios`} />
				</MenuItem>
			)}
			<MenuItem className={classes.menuItem} onClick={(e) => handleResolveClick(e, { sendFarewell: false })}>
				<ListItemIcon>
					<CheckCircleOutlineIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Resolver sin despedida" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={(e) => { closeActionMenu(); handleUpdateTicketStatus(e, "pending", null); }}>
				<ListItemIcon>
					<UndoRoundedIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Devolver" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleOpenTransferModal}>
				<ListItemIcon>
					<PeopleIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary={i18n.t("ticketOptionsMenu.transfer")} />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleOpenAppointmentModal}>
				<ListItemIcon>
					<HourglassEmptyIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Posponer" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleSelectMessages}>
				<ListItemIcon>
					<CheckIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Seleccionar mensajes" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleMarkAsUnread}>
				<ListItemIcon>
					<MarkunreadIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Marcar como no leído" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleOpenContactDetails}>
				<ListItemIcon>
					<PersonIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Detalles de contacto" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleOpenTagsModal}>
				<ListItemIcon>
					<LocalOffer fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Motivos del Contacto" />
			</MenuItem>
			<MenuItem className={classes.menuItem} onClick={handleKeepWithMe}>
				<ListItemIcon>
					<LockIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Mantener conmigo" />
			</MenuItem>
			{!embedded && ticket.uuid ? (
				<MenuItem className={classes.menuItem} onClick={openTicketInPopup}>
					<ListItemIcon>
						<OpenInNew fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={i18n.t("ticketOptionsMenu.openInNewWindow")} />
				</MenuItem>
			) : null}
			{user?.profile === "admin" ? (
				<>
					<Divider />
					<MenuItem className={`${classes.menuItem} ${classes.dangerItem}`} onClick={handleOpenConfirmationModal}>
						<ListItemIcon>
							<Delete fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Borrar" />
					</MenuItem>
				</>
			) : null}
		</Menu>
	);

	return (
		<>
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<>
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
					{ticket.uuid && !embedded ? (
						<Tooltip title={i18n.t("ticketOptionsMenu.openInNewWindow")}>
							<IconButton
								onClick={openTicketInPopup}
								className={classes.modernIconButton}
								aria-label={i18n.t("ticketOptionsMenu.openInNewWindow")}
							>
								<OpenInNew />
							</IconButton>
						</Tooltip>
					) : null}
				</>
			)}
			{ticket.status === "open" && (
				<>
					<ButtonGroup variant="contained" className={classes.resolveGroup} disabled={loading}>
						<Button
							onClick={handleResolveClick}
							className={classes.resolveButton}
							size="small"
						>
							{i18n.t("messagesList.header.buttons.resolve")}
						</Button>
						<Button
							className={classes.resolveMenuButton}
							size="small"
							aria-haspopup="true"
							aria-label="Más opciones"
							onClick={(e) => {
								e.stopPropagation();
								setMenuAnchorEl(e.currentTarget);
							}}
						>
							<ArrowDropDownIcon />
						</Button>
					</ButtonGroup>
					{renderResolveMenu()}
				</>
			)}
			{ticket.status === "pending" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.accept")}
					</ButtonWithSpinner>
					{ticket.uuid ? (
						<>
							<Tooltip title={i18n.t("ticketOptionsMenu.schedule")}>
								<IconButton
									onClick={handleOpenAppointmentModal}
									className={classes.modernIconButton}
									aria-label={i18n.t("ticketOptionsMenu.schedule")}
								>
									<EventNote />
								</IconButton>
							</Tooltip>
							{!embedded && (
							<Tooltip title={i18n.t("ticketOptionsMenu.openInNewWindow")}>
								<IconButton
									onClick={openTicketInPopup}
									className={classes.modernIconButton}
									aria-label={i18n.t("ticketOptionsMenu.openInNewWindow")}
								>
									<OpenInNew />
								</IconButton>
							</Tooltip>
							)}
						</>
					) : null}
				</>
			)}
		</div>
		<NegocioCustomerMenu ticket={ticket} menuOpen={Boolean(menuAnchorEl)} open={negocioOpen} onClose={() => setNegocioOpen(false)} onAvailable={setNegocioCustomer} />
		<ConfirmationModal
			title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
				ticket.id
			} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
				ticket.contact?.name || i18n.t("ticketOptionsMenu.confirmationModal.unknownContact")
			}?`}
			open={confirmationOpen}
			onClose={setConfirmationOpen}
			onConfirm={handleDeleteTicket}
		>
			{i18n.t("ticketOptionsMenu.confirmationModal.message")}
		</ConfirmationModal>
		<ConfirmationModal
			title={i18n.t("ticketsList.buttons.confirmCloseWithTagsTitle")}
			open={closeTicketConfirmOpen}
			onClose={() => setCloseTicketConfirmOpen(false)}
			onConfirm={handleConfirmCloseTicketModal}
		>
			{getCloseConfirmModalBody()}
		</ConfirmationModal>
		<TransferTicketModalCustom
			modalOpen={transferTicketModalOpen}
			onClose={handleCloseTransferTicketModal}
			ticketid={ticket.id}
		/>
		<AppointmentModal
			open={appointmentModalOpen}
			onClose={handleCloseAppointmentModal}
			appointment={null}
			ticketId={ticket.id}
			initialWhatsappId={ticket.whatsappId}
			initialContact={ticket.contact}
			onSaved={() => {}}
		/>
		<TagsModal
			open={tagsModalOpen}
			onClose={handleCloseTagsModal}
			ticketId={ticket.id}
			ticket={ticket}
		/>
	</>
	);
};

export default TicketActionButtonsCustom;
